/*
 * <custom-media-pips> — dot pagination for the PDP mobile media slider.
 *
 * Rendered by snippets/custom-media-pips.liquid inside the gallery's
 * `.slider-buttons`. Dawn's base SliderComponent (global.js) only writes the
 * "1 / 5" counter; it syncs an active dot in SlideshowComponent, which the
 * product gallery does not use. Rather than fork either, this element rides on
 * the `slideChanged` CustomEvent the base component already dispatches, and
 * scrolls the slider the same way Dawn's own arrow handler does.
 *
 * The first dot is server-rendered active, which is always correct: the gallery
 * puts the selected variant's featured media first.
 *
 * Image-heavy products would run the dots off both edges of a phone, so past
 * `maxVisible` the row becomes a sliding window centred on the active dot, with
 * the edge dots shrunk to signal more in that direction. Everything below that
 * count behaves as before. The CSS keeps the row wrapping rather than
 * overflowing, so the no-JS render never bleeds off the sides either.
 */
class CustomMediaPips extends HTMLElement {
  // Odd, so the active dot can sit dead centre of the window.
  static maxVisible = 7;

  connectedCallback() {
    this.sliderComponent = this.closest('slider-component');
    this.pips = Array.from(this.querySelectorAll('.slider-counter__link'));

    if (!this.sliderComponent || this.pips.length < 2) return;

    this.slider = this.sliderComponent.querySelector('[id^="Slider-"]');
    if (!this.slider) return;

    this.onSlideChanged = this.slideChangedHandler.bind(this);
    this.sliderComponent.addEventListener('slideChanged', this.onSlideChanged);
    this.pips.forEach((pip, index) => pip.addEventListener('click', this.scrollToSlide.bind(this, index)));

    this.applyWindow(0);
  }

  disconnectedCallback() {
    if (this.onSlideChanged) this.sliderComponent.removeEventListener('slideChanged', this.onSlideChanged);
  }

  slideChangedHandler(event) {
    this.setActivePip(event.detail.currentPage - 1);
  }

  setActivePip(index) {
    const active = Math.min(Math.max(index, 0), this.pips.length - 1);

    this.pips.forEach((pip, i) => {
      pip.classList.toggle('slider-counter__link--active', i === active);
      if (i === active) {
        pip.setAttribute('aria-current', 'true');
      } else {
        pip.removeAttribute('aria-current');
      }
    });

    this.applyWindow(active);
  }

  // Keeps at most `maxVisible` dots on screen, centred on the active one and
  // clamped at both ends of the strip. A visible dot with more beyond it in that
  // direction is marked as an edge and shrinks, so the row reads as continuing.
  applyWindow(active) {
    const { maxVisible } = CustomMediaPips;
    if (this.pips.length <= maxVisible) return;

    const start = Math.min(Math.max(active - Math.floor(maxVisible / 2), 0), this.pips.length - maxVisible);
    const end = start + maxVisible - 1;

    this.pips.forEach((pip, i) => {
      const isVisible = i >= start && i <= end;
      const isEdge = isVisible && ((i === start && start > 0) || (i === end && end < this.pips.length - 1));

      pip.hidden = !isVisible;
      pip.classList.toggle('custom-media-pips__link--edge', isEdge);
    });
  }

  scrollToSlide(index) {
    // Recomputed per click: variant changes can re-render the slide list.
    const slides = Array.from(this.slider.querySelectorAll('[id^="Slide-"]')).filter((slide) => slide.clientWidth > 0);
    const target = slides[index];
    if (!target) return;

    // Matches Dawn's arrow maths (offset from the first slide). The slider's own
    // `scroll-behavior: smooth` animates it, and the resulting scroll fires
    // slideChanged, which is what marks the dot active.
    this.slider.scrollTo({ left: target.offsetLeft - slides[0].offsetLeft });
  }
}

customElements.define('custom-media-pips', CustomMediaPips);
